export class Cache {
  constructor () {
    /** @type {Map<String, String>} */
    this.cache = new Map()
  }

  exists (key) {
    return this.cache.has(key)
  }

  /**
   * @param {String} key
   * @param {String} value
   */
  set (key, value) {
    this.cache.set(key, value)
  }

  /**
   * @param {String} key
   */
  get (key) {
    return this.cache.get(key)
  }

  delete (key) {
    this.cache.delete(key)
  }
}

export class DataFetcher {
  constructor () {
    this.cache = new Cache()
  }

  /**
   * @param {String} url
   * @param {String} section
   */
  async fetch (url, section) {
    return this.cache.exists(url)
      ? this.cache.get(url)
      : await this.fetchData(url, section)
  }

  /**
   * @param {String} url
   * @param {String} section
   *
   * @returns {Promise<String>}
   */
  async fetchData (url, section) {
    const html = await window.BAO.sectionRenderer.fetchSingle(section, url)

    this.cache.set(url, html)

    return html
  }
}

export class Renderer {
  /**
   * @param {CollectionList} element
   */
  constructor (element) {
    this.dataFetcher = new DataFetcher()

    /**
     * @type {CollectionList}
     * @public
     */
    this.element = element

    this.currentLowestPage =
      Number(this.element.els.items.element.dataset.currentPage) || 1
    this.currentHighestPage = this.currentLowestPage
  }

  /**
   * @param {String} url
   * @param {'NEXT'|'PREVIOUS'} [direction]
   */
  async render (url, direction = 'NEXT') {
    const html = await this.dataFetcher.fetch(
      url,
      this.element.section.section,
    )

    this.element.log('render', { url, html })

    const newHTMLDom = new DOMParser().parseFromString(html, 'text/html')

    this.renderItems(newHTMLDom, direction)
    this.renderItemCount(newHTMLDom)
    this.renderLoadMoreState(newHTMLDom)
    this.renderLoadPreviousState(newHTMLDom)

    if (this.currentPage < this.currentLowestPage) {
      this.currentLowestPage = this.currentPage
    }

    if (this.currentPage > this.currentHighestPage) {
      this.currentHighestPage = this.currentPage
    }
  }

  /**
   * @param {Document} document
   * @param {'NEXT'|'PREVIOUS'} [direction]
   */
  renderItems (document, direction = 'NEXT') {
    const newItems = document.querySelectorAll(
      this.element?.els?.item?.selector,
    )
    if (!newItems) return

    this.element.log('renderItems', { newItems, direction })

    const itemsContainer = this.element.els.items.element
    const fragment = new DocumentFragment()

    newItems.forEach((item) => fragment.append(item))

    if (direction === 'NEXT') {
      itemsContainer.append(fragment)
    } else if (direction === 'PREVIOUS') {
      itemsContainer.prepend(fragment)
    }
  }

  /**
   * @param {Document} document
   */
  renderItemCount (document) {
    const count = document.querySelector(
      this.element?.els['pagination-count']?.selector,
    )?.innerHTML

    this.element.log('renderItemCount', { count })

    if (!count) return

    this.element.els['pagination-count'].elements.forEach((el) => {
      el.innerHTML = count
    })
  }

  /**
   * @param {Document} document
   */
  renderLoadMoreState (document) {
    const loadMore = document.querySelector(
      this.element?.els['load-more']?.selector,
    )

    this.element.log('renderLoadMoreState', { loadMore })

    if (!loadMore) return

    if (this.shouldRerenderLoadMore) {
      const [path, searchString] = loadMore.getAttribute('href').split('?')
      const searchParams = new URLSearchParams(searchString)
      searchParams.page = this.currentHighestPage

      const isDisabled = loadMore.hasAttribute('disabled')

      this.element.els['load-more'].element.setAttribute(
        'href',
        `${path}?${searchParams.toString()}`,
      )
      this.element.els['load-more'].element.toggleAttribute(
        'disabled',
        isDisabled,
      )

      if (isDisabled) {
        this.element.els['load-more'].element.innerText =
          window.theme.strings.pagination.showingAllCollections
      } else {
        this.element.els['load-more'].element.innerText =
          window.theme.strings.pagination.loadMore
      }
    }
  }

  /**
   * @param {Document} document
   */
  renderLoadPreviousState (document) {
    if (!this.shouldShowLoadPrevious) {
      this.element.els['load-previous-container'].element.innerHTML = ''

      return
    }

    const loadPrevious = document.querySelector(
      this.element?.els['load-previous']?.selector,
    )

    this.element.log('renderLoadPreviousState', { loadPrevious })

    if (!loadPrevious) return

    if (this.loadPreviousIsCurrentlyHidden) {
      this.element.els['load-previous-container'].element.innerHTML =
        document.querySelector(
          this.element?.els['load-previous-container']?.selector,
        ).innerHTML

      return
    }

    if (this.shouldRerenderLoadPrevious) {
      const [path, searchString] = loadPrevious.getAttribute('href').split('?')
      const searchParams = new URLSearchParams(searchString)
      searchParams.page = this.currentLowestPage

      const isDisabled = loadPrevious.hasAttribute('disabled')
      const url = searchParams.toString()
        ? `${path}?${searchParams.toString()}`
        : path

      this.element.els['load-previous'].element.setAttribute('href', url)
      this.element.els['load-previous'].element.toggleAttribute(
        'disabled',
        isDisabled,
      )
    }
  }

  get shouldShowLoadPrevious () {
    const anElementFromTheFirstPageExists = !!window.BAO.Utils?.getElement(
      `${this.element.els.item?.selector}[data-page="1"]`,
    )
    if (anElementFromTheFirstPageExists) return false

    return this.currentPage > 1
  }

  get loadPreviousIsCurrentlyHidden () {
    return !this.element.els['load-previous-container'].element.innerHTML
  }

  get shouldRerenderLoadPrevious () {
    return this.currentPage < this.currentLowestPage
  }

  get shouldRerenderLoadMore () {
    return this.currentPage >= this.currentHighestPage
  }

  get currentPage () {
    const params = this.element.searchParams.current
    const page = params.get('page')

    return Number(page) || 1
  }

  /**
   * @returns {number}
   */
  get lastPage () {
    return Number(this.element.els.items.element.dataset.lastPage)
  }
}

class CollectionList extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['item', 'items', 'load-more-container', 'load-previous-container']
  }

  constructor () {
    super()

    this.renderer = new Renderer(this)

    /**
     * @type {Section}
     * @public
     */
    this.section = {
      id: 'template-blog',
      section: this.dataset.id,
      selector: '.shopify-section',
    }

    /**
     * @type {{ previous: URLSearchParams, current: URLSearchParams, initial: URLSearchParams }}
     * @public
     */
    this.searchParams = {
      // eslint-disable-next-line compat/compat
      previous: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      current: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      initial: new URLSearchParams(window.location.search),
    }
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(
      this.els['load-more-container'].element,
      'click',
      this.onLoadMoreClick.bind(this),
    )
    this.listeners.add(
      this.els['load-previous-container'].element,
      'click',
      this.onLoadPreviousClick.bind(this),
    )

    if ('back-to-top' in this.els) {
      this.listeners.add(
        this.els['back-to-top'].element,
        'click',
        this.onBackToTopClick.bind(this),
      )
    }
  }

  onLoadMoreClick (event) {
    const loadMoreButton = event.target.closest(this.els['load-more'].selector)

    if (!loadMoreButton) return

    event.preventDefault()

    if (loadMoreButton?.hasAttribute('disabled')) return

    const anchor = event.target.closest('a')
    this.log('onLoadMoreClick', { anchor })
    if (
      !anchor ||
      anchor.getAttribute('href') === '' ||
      anchor.hasAttribute('disabled')
    ) {
      return
    }
    const href = anchor.getAttribute('href')
    this.log('onLoadMoreClick', { href })

    const paginationSearchParams = new URLSearchParams(href.split('?')[1])
    const wantedPage = paginationSearchParams.get('page') ?? '1'

    const currentSearchParams = new URLSearchParams(
      this.searchParams.current.toString(),
    )
    currentSearchParams.set('page', wantedPage)

    this.renderPage({
      searchParams: currentSearchParams.toString(),
      direction: 'NEXT',
    })
  }

  onLoadPreviousClick (event) {
    if (event.target.closest(this.els['load-previous'].selector) == null) {
      return
    }

    event.preventDefault()

    const anchor = event.target.closest('a')
    this.log('onLoadPreviousClick', { anchor })
    if (
      !anchor ||
      anchor.getAttribute('href') === '' ||
      anchor.hasAttribute('disabled')
    ) {
      return
    }
    const href = anchor.getAttribute('href')
    this.log('onLoadPreviousClick', { href })

    const paginationSearchParams = new URLSearchParams(href.split('?')[1])
    const wantedPage = paginationSearchParams.get('page') ?? '1'

    const currentSearchParams = new URLSearchParams(
      this.searchParams.current.toString(),
    )
    currentSearchParams.set('page', wantedPage)

    this.renderPage({
      searchParams: currentSearchParams.toString(),
      direction: 'PREVIOUS',
    })
  }

  /**
   * @param {Object}
   * @param {String} searchParams
   * @param {InputEvent} [e]
   * @param {Boolean} [updateURLHash]
   * @param {'NEXT'|'PREVIOUS'} [direction]
   *
   * @returns {void}
   */
  async renderPage ({
    searchParams,
    e,
    updateURLHash = true,
    direction = 'NEXT',
  } = {}) {
    this.log('renderPage', { searchParams, e, updateURLHash })

    this.searchParams.previous = this.searchParams.current
    // eslint-disable-next-line compat/compat
    this.searchParams.current = new URLSearchParams(searchParams)

    this.loading = true

    const url = `${window.location.pathname}?section_id=${
      this.section.section
    }&${this.searchParams.current.toString()}`
    this.log('renderPage', { section: this.section, url })

    await this.renderer.render(url, direction)

    this.loading = false

    if (updateURLHash) this.updateURLHash(this.searchParams.current)
  }

  /**
   * @param {URLSearchParams} searchParams
   *
   * @returns {void}
   */
  updateURLHash (searchParams) {
    if (searchParams.get('page') === '1') searchParams.delete('page')

    history.pushState(
      { searchParams: searchParams.toString() },
      '',
      `${window.location.pathname}${
        searchParams.toString() && '?'.concat(searchParams.toString())
      }`,
    )
  }

  onBackToTopClick (event) {
    event.preventDefault()

    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  /**
   * @returns {Boolean}
   */
  get loading () {
    return this._loading
  }

  /**
   * @param {Boolean} val
   *
   * @returns {void}
   */
  set loading (val) {
    this._loading = val

    this.setAttribute('aria-busy', val.toString())
  }

  /**
   * @return {Number}
   */
  get itemCount () {
    return Number(this.dataset.totalItemCount) || 0
  }

  /**
   * @return {String}
   */
  get countString () {
    return (
      this.dataset.countString ||
      '{{ current }} of {{ total }} collections shown'
    )
  }
}

if (!customElements.get('collection-list')) {
  customElements.define('collection-list', CollectionList)
}
